// Per-position rationale + setup tag, debounced auto-save. Edgewonk pattern.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, NotebookPen } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

const SETUPS = ['swing', 'long-term', 'speculative', 'income', 'hedge'] as const;
type Setup = (typeof SETUPS)[number];

type Props = { ticker: string };

export function JournalCard({ ticker }: Props) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['journal', ticker],
    queryFn: () => api.getJournal(ticker),
  });
  const [rationale, setRationale] = useState('');
  const [tag, setTag] = useState<Setup | null>(null);
  const [saved, setSaved] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (q.data && !initialized.current) {
      setRationale(q.data.rationale);
      setTag((q.data.setupTag as Setup | null) ?? null);
      initialized.current = true;
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: { setupTag?: string | null; rationale?: string }) =>
      api.putJournal(ticker, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['journal', ticker] });
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    },
  });

  // Debounced save when rationale changes
  useEffect(() => {
    if (!initialized.current) return;
    if (q.data?.rationale === rationale) return;
    const t = setTimeout(() => {
      save.mutate({ rationale });
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rationale]);

  const setTagAndSave = (next: Setup | null) => {
    setTag(next);
    save.mutate({ setupTag: next });
  };

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <NotebookPen className="size-4 text-indigo-300" />
          <h3 className="text-sm font-medium text-zinc-200">Journal</h3>
        </div>
        {saved && (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
            <Check className="size-3" /> Saved
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1 mb-2">
        {SETUPS.map((s) => (
          <button
            type="button"
            key={s}
            onClick={() => setTagAndSave(tag === s ? null : s)}
            className={cn(
              'text-[11px] px-2 py-1 rounded-full border transition-colors capitalize',
              tag === s
                ? 'border-indigo-500/60 bg-indigo-500/15 text-indigo-100'
                : 'border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700',
            )}
          >
            {s}
          </button>
        ))}
      </div>

      <textarea
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        placeholder="Why am I in this trade? Stop logic, target, setup, what I'd revise on."
        rows={5}
        className="w-full bg-zinc-900/50 border border-zinc-800 rounded-md p-2 text-xs text-zinc-100 outline-none focus:border-indigo-500/60 placeholder:text-zinc-400 resize-none"
      />
      <div className="text-[10px] text-zinc-400 mt-1">
        {q.data?.updatedAt ? `Last saved ${new Date(q.data.updatedAt).toLocaleString('en-GB')}` : ''}
      </div>
    </div>
  );
}
